import { Component } from '@theme/component';
import { ThemeEvents, RecentlyViewedRemovedEvent } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const AUTO_DISMISS_MS = 4000;

/**
 * A toast that confirms a product was removed from recently viewed, with an undo action.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The product thumbnail.
 * @property {HTMLElement} title - The bold product title.
 * @property {HTMLElement} message - The text following the product title.
 * @property {HTMLButtonElement} undoButton - Re-adds the product, shown when one was removed.
 *
 * @extends {Component<Refs>}
 */
class RecentlyViewedToast extends Component {
  requiredRefs = ['image', 'title', 'message', 'undoButton'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #dismissTimeout;

  /** @type {string | undefined} The product id to restore if the visitor clicks "Ongedaan maken". */
  #removedProductId;

  /** @type {number | undefined} The index to restore it at, so it lands back in its original spot. */
  #removedIndex;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.recentlyViewedRemoved, this.#onRecentlyViewedRemoved);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.recentlyViewedRemoved, this.#onRecentlyViewedRemoved);
    clearTimeout(this.#dismissTimeout);
  }

  /**
   * Shows the toast for the removed product, restarting the auto-dismiss timer.
   * @param {Event} event - The recently-viewed-removed event.
   */
  #onRecentlyViewedRemoved = (event) => {
    if (!(event instanceof RecentlyViewedRemovedEvent)) return;

    this.#removedProductId = event.detail.productId;
    this.#removedIndex = event.detail.index;

    // An empty src would otherwise have the browser re-request the current page.
    this.refs.image.src = event.detail.image || 'data:,';
    this.refs.image.alt = event.detail.title;
    this.refs.title.textContent = event.detail.title;
    this.refs.message.textContent = this.dataset.messageRemoved ?? '';

    clearTimeout(this.#dismissTimeout);
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    this.#dismissTimeout = setTimeout(this.dismiss, AUTO_DISMISS_MS);
  };

  /** Restores the just-removed product back into its original spot, and dismisses the toast. */
  undo = () => {
    if (this.#removedProductId) RecentlyViewed.addProduct(this.#removedProductId, this.#removedIndex);
    this.dismiss();
  };

  /** Hides the toast, playing the slide-down animation. */
  dismiss = () => {
    clearTimeout(this.#dismissTimeout);
    this.classList.add('is-closing');
    this.classList.remove('is-open');
  };
}

if (!customElements.get('recently-viewed-toast')) {
  customElements.define('recently-viewed-toast', RecentlyViewedToast);
}
